import React from "react";
import { currencyFormat } from "helpers/currency-utils";
import {
  Divider,
  Icon,
  Button,
  Box,
  Grid2,
  Container,
  Card,
  CardContent,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import ActionButton from "components/atoms/action-button/action-button";
import {
  removeItem,
  UpdateRentalItemsResponse,
} from "services/rentals-thin.service";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { GlobalState, Totals, store, updateTotals } from "store/rentals-store";
import { Path } from "components/app.component";
import { getStateWithItems } from "test/context-utils";

export default function PurchaseSummary() {
  const path: string = useSelector(
    (state: GlobalState) => state.navigation.currentPath
  );
  const totals: Totals = useSelector(
    (state: GlobalState) => state.rental.totals
  );

  const navigation = useNavigate();

  async function removeFromCart(carId: string) {
    try {
      const res: UpdateRentalItemsResponse = await removeItem(carId);
      store.dispatch(updateTotals(res.data));
      if (res.data.items.length === 0 && path !== Path.Home) {
        navigation(Path.Home);
      }
    } catch (error) {
      console.error("Fail to remove item");
    }
  }

  return (
    <Container>
      {totals && totals.items.length > 0 ? (
        <Box>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                {/* Header */}
                <Typography variant="h6" fontWeight={600}>
                  Resumen de compra
                </Typography>

                <Divider />

                {/* Rental Days */}
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Duración</Typography>
                  <Chip
                    data-testid="rental-days"
                    label={`${totals.days} día${totals.days > 1 ? "s" : ""}`}
                    color="primary"
                    size="small"
                  />
                </Stack>

                {/* Items */}
                <List disablePadding>
                  {totals.items.map((item, index) => (
                    <React.Fragment key={`item-${item.id}`}>
                      <ListItem
                        data-testid={`item-${item.id}`}
                        disableGutters
                        sx={{ px: 0, py: 0.5 }}
                      >
                        <ListItemText
                          data-testid={`price-${item.id}`}
                          primary={item.name}
                          secondary={`${currencyFormat(item.price)} / día`}
                        />
                        <Typography
                          fontWeight={500}
                          sx={{ display: "flex", flexDirection: "column" }}
                        >
                          {currencyFormat(item.price * totals.days)}
                          {path !== Path.Confirmation && (
                            <Button
                              data-testid={`remove-${item.id}`}
                              sx={{ padding: 0 }}
                              onClick={() => removeFromCart(item.id)}
                            >
                              <Icon>delete_forever</Icon>
                            </Button>
                          )}
                        </Typography>
                      </ListItem>
                      {item.depositFee && (
                        <ListItem
                          key={`deposit-${item.id}`}
                          disableGutters
                          sx={{ px: 0, py: 0 }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" color="text.secondary">
                                Depósito de seguridad <br /> (Reembolsable si no hay daños)
                              </Typography>
                            }
                          />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {currencyFormat(item.depositFee)}
                          </Typography>
                        </ListItem>
                      )}
                    </React.Fragment>
                  ))}
                </List>

                <Divider />

                {/* Total */}
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle1" fontWeight={600}>
                    Total
                  </Typography>
                  <Typography
                    data-testid="amount-due"
                    variant="subtitle1"
                    fontWeight={700}
                    color="primary"
                  >
                    {currencyFormat(totals.amountDue)}
                  </Typography>
                </Stack>

                {/* Small disclaimer */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                >
                  Cargos por impuestos y tarifas extra podrían aplicar
                </Typography>
              </Stack>
                <Grid2 sx={{ display: { xs: "none", md: "block" } }} size={{ xs: 12 }} className="center-align">
                  <ActionButton
                    testid="set-rental-btn"
                    className="purchase-summary-btn"
                  />
                </Grid2>
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Box>
          <h2 className="flow-text">No ha seleccionado ningún auto</h2>
        </Box>
      )}
    </Container>
  );
}
